import React from "react";
import { MessageCircle, Share2, MapPin, Heart, Zap, BadgeCheck, ShoppingCart } from "lucide-react";
import { fileUrl } from "@/lib/uploads";
import { BlurImage } from "@/lib/blur";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatPrice, formatApprox, convert, getBuyerCurrency, useFxRates } from "@/lib/currency";
import { toast } from "sonner";

export default function ProductCard({ product, currency, onMessage, onOpen, onAddToCart, onToggleSave }) {
  const { t } = useI18n();
  useFxRates();
  const img = (product.images || [])[0];
  const sellable = !!product.sellable;
  const cur = product.currency || currency || "USD";
  const base = Number(product.base_price ?? product.price ?? 0);
  const price = Number(product.effective_price ?? product.price ?? 0);
  const onPromo = !!product.on_promo && price < base;
  const pct = onPromo && base > 0 ? Math.round((1 - price / base) * 100) : 0;
  const outOfStock = product.in_stock === false;
  const lowStock = product.stock_tracked && product.in_stock && typeof product.total_stock === "number" && product.total_stock > 0 && product.total_stock <= 5;
  const buyerCur = getBuyerCurrency();
  const approx = buyerCur !== cur && price > 0 ? convert(price, cur, buyerCur) : null;
  const share = async (e) => {
    e.stopPropagation();
    const link = `${window.location.origin}/app?product=${product.id}`;
    try {
      if (navigator.share) await navigator.share({ title: product.title, text: `${product.title} — ${formatPrice(price, cur)}`, url: link });
      else { await navigator.clipboard.writeText(link); toast.success(t("m_link_copied")); }
    } catch {}
  };
  const toggleSave = async (e) => {
    e.stopPropagation();
    try { const { data } = await api.post(`/market/products/${product.id}/save`); toast.success(data.saved ? t("m_saved_wishlist") : t("m_removed")); onToggleSave && onToggleSave(product.id, data.saved); } catch {}
  };
  return (
    <div onClick={() => onOpen && onOpen(product)} data-testid={`product-${product.id}`}
      className="relative rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-surface)] overflow-hidden cursor-pointer">
      {product.boosted && <span data-testid={`product-boosted-${product.id}`} className="absolute top-2 left-2 z-10 flex items-center gap-1 rounded-full bg-[var(--rc-gold)] text-black text-[10px] font-bold px-2 py-0.5"><Zap size={11} /> {t("m_boosted")}</span>}
      {onPromo && !product.boosted && <span data-testid={`product-promo-${product.id}`} className="absolute top-2 left-2 z-10 rounded-full bg-red-500 text-white text-[10px] font-bold px-2 py-0.5">-{pct}% {t("m_off")}</span>}
      <button onClick={toggleSave} data-testid={`product-save-${product.id}`} className="absolute top-2 right-2 z-10 h-8 w-8 grid place-items-center rounded-full bg-black/50 backdrop-blur">
        <Heart size={16} className={product.saved ? "fill-[var(--rc-gold)] text-[var(--rc-gold)]" : "text-white"} />
      </button>
      <div className="relative aspect-square bg-[var(--rc-bg)]">
        {img ? <BlurImage src={fileUrl(img.url)} hash={img.blur_hash} className="h-full w-full" imgClassName="h-full w-full object-cover" testid={`product-img-${product.id}`} />
          : <div className="h-full w-full grid place-items-center text-[var(--rc-muted)] text-4xl">🛍️</div>}
        {outOfStock && <div className="absolute inset-0 bg-black/55 grid place-items-center" data-testid={`product-oos-${product.id}`}><span className="rounded-full bg-black/70 text-white text-xs font-bold px-3 py-1">{t("m_out_of_stock")}</span></div>}
        {sellable && !outOfStock && (
          <button onClick={(e) => { e.stopPropagation(); onAddToCart && onAddToCart(product); }} data-testid={`product-cart-${product.id}`} title={t("m_add_to_cart")}
            className="absolute bottom-2 right-2 z-10 h-9 w-9 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black shadow-lg active:scale-90 transition-transform">
            <ShoppingCart size={17} />
          </button>
        )}
      </div>
      <div className="p-3">
        <div className="font-medium truncate flex items-center gap-1">{product.title}{product.shop_verified && <BadgeCheck size={14} className="text-[var(--rc-gold)] shrink-0" data-testid={`product-verified-${product.id}`} />}</div>
        {base > 0 ? (
          <div className="flex items-baseline gap-2">
            <span className="text-[var(--rc-gold)] font-semibold" data-testid={`product-price-${product.id}`}>{formatPrice(price, cur)}</span>
            {onPromo && <span className="text-xs text-[var(--rc-muted)] line-through">{formatPrice(base, cur)}</span>}
          </div>
        ) : <div className="text-[var(--rc-gold)] font-semibold">—</div>}
        {approx != null && <div className="text-[11px] text-[var(--rc-muted)] mt-0.5" data-testid={`product-approx-${product.id}`}>{formatApprox(approx, buyerCur)}</div>}
        {lowStock && <div className="text-[11px] text-orange-400 font-semibold mt-0.5">{t("m_only_left").replace("{n}", product.total_stock)}</div>}
        {product.location && <div className="text-xs text-[var(--rc-muted)] flex items-center gap-1 mt-0.5"><MapPin size={11} /> {product.location}</div>}
        <div className="flex items-center gap-2 mt-2">
          <button onClick={(e) => { e.stopPropagation(); onMessage && onMessage(product); }} data-testid={`product-message-${product.id}`}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-[var(--rc-gold)] text-black text-xs font-semibold py-2"><MessageCircle size={14} /> {t("m_message")}</button>
          <button onClick={share} data-testid={`product-share-${product.id}`} className="h-8 w-8 grid place-items-center rounded-full border border-[var(--rc-border)] text-[var(--rc-muted)]"><Share2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}
